import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Formik, Form } from 'formik'
import { useTranslation } from 'react-i18next'

// Types
import { ICompanyMember, ICompany } from '../../common/models/Company'
import { IGoal, PdpStatus, GoalChatMessageType } from '../../common/models/Pdp'
import { IconType } from '../icon/IconType'

// Styles
import { mqFrom } from '../../styles/mediaQueries'
import { shadows } from '../../styles/effects'
import { hsla } from '../../styles/colors'

// Components
import Text, { MarkDownText } from '../Text'
import Button from '../Button'
import GoalJournal from './Journal'
import Input from '../formControl/formik/FormikTextarea'
import Avatar from '../Avatar'
import Icon from '../icon/Icon'
import { RowAligned } from '../layout/LayoutAtoms'
import { ISection, ContextMenu } from '../ContextButton'
import Fold from '../Fold'
import EvaluationHeader from '../EvaluationHeader'

// Utils
import {
  canMoreMessagesBeSentInPdpChat,
  hasGoalActiveNotifications,
} from '../../common/helpers/pdpBusiness.helper'
import { useAppState, useAppDispatch, ITheme } from '../../context/AppContext'
import markGoalMessagesOpen from '../../context/services/pdp/markGoalMessagesOpen.service'
import { cultureValueColors } from '../../utils/habitHelper'

export interface IGoalType {
  title: string
  icon?: IconType
  color: string
  background: string
}

interface IProps extends IGoal {
  className?: string
  goalType: IGoalType
  contextMenu?: ISection[]
  title: string
  description: string
  tags?: React.ReactNode
  member: ICompanyMember
  reviewer?: ICompanyMember
  isInEditMode?: boolean
  renderEditMode?: React.ReactNode
  isOwnProfile: boolean
  submitMessage?: (id: string, message: string) => void
  pdpStatus?: PdpStatus
  pdpId?: string
  bottomContent?: React.ReactNode
  openJournals?: boolean
  openDescription?: boolean
  withEvaluationResults?: boolean
  evaluationDone?: boolean
  cta?: {
    icon?: IconType
    label: string
    onClick: () => void
  }
}

export type ICardHeaderProps = Pick<IProps, 'contextMenu' | 'tags'> & {
  headerType: IGoalType
  onClick?: () => void
  className?: string
}

export const getGoalType = (
  company: ICompany,
  theme: ITheme,
  cultureId?: string,
  skillId?: string
): IGoalType => {
  const defaultType = {
    title: '',
    color: theme.colors.text72,
    background: theme.colors.lightest,
  }
  if (cultureId) {
    const value = company.allCultureValues.find((el) => el._id === cultureId)
    if (!value) return defaultType
    const { darkValueColor, lightValueColor } = cultureValueColors(
      theme,
      value.color,
      company
    )

    return {
      title: value.name,
      icon: 'Culture',
      color: darkValueColor,
      background: lightValueColor,
    }
  } else if (skillId) {
    const skill = company.skills
      .concat(company.teamLeadSkills)
      .find((el) => el._id === skillId)
    if (!skill) return defaultType

    return {
      title: skill.name,
      icon: 'Skills',
      color: theme.colors.text,
      background: theme.colors.textLightest,
    }
  } else {
    return defaultType
  }
}

export const CardHeader: React.FC<ICardHeaderProps> = ({
  headerType,
  tags,
  contextMenu,
  onClick,
  className,
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const { t } = useTranslation()

  const closeMenu = () => setShowMenu(false)

  useEffect(() => {
    if (!window || !contextMenu) return
    window.addEventListener('click', closeMenu)

    return () => {
      window.removeEventListener('click', closeMenu)
    }
  }, [])

  return (
    <Header
      bgColor={headerType.background}
      withBorder={!headerType.icon}
      className={className}
      onClick={onClick}
    >
      {headerType.icon && (
        <Icon type={headerType.icon} size={16} color={headerType.color} />
      )}
      <StyledText textColor={headerType.color} textStyle="subtitle">
        {headerType.title.length === 0
          ? t('general.goals.generalGoal')
          : headerType.title}
      </StyledText>
      {tags && <Tags>{tags}</Tags>}
      {contextMenu && (
        <StyledButton
          caption="•••"
          newTextColor={headerType.color}
          hoverTextColor="primary"
          hoverColor="lightest"
          onClick={(e) => {
            e?.stopPropagation()
            setShowMenu(!showMenu)
          }}
        />
      )}
      {showMenu && contextMenu && (
        <ContextMenu sections={contextMenu} closeMenu={closeMenu} />
      )}
    </Header>
  )
}

const GoalCard: React.FC<IProps> = ({
  className,
  goalType,
  title,
  description,
  contextMenu,
  isInEditMode,
  renderEditMode,
  tags,
  isOwnProfile,
  member,
  reviewer,
  bottomContent,
  pdpStatus,
  submitMessage,
  cta,
  pdpId,
  openJournals,
  openDescription,
  withEvaluationResults,
  evaluationDone,
  ...goal
}) => {
  const { t } = useTranslation()
  const { theme, userAuthToken, company, user } = useAppState()
  const appDispatch = useAppDispatch()

  const notificationNumber = goal.journals.filter(
    (journal) =>
      journal.messageType === GoalChatMessageType.SIMPLE_MESSAGE ||
      journal.messageType === GoalChatMessageType.EVALUATION_RESULT
  ).length
  const showNotification = hasGoalActiveNotifications(
    member,
    isOwnProfile,
    goal
  )

  const openJournalFnc = async () => {
    if (!pdpId || !isOwnProfile || !showNotification) return
    const request = await markGoalMessagesOpen(
      userAuthToken || '',
      pdpId,
      goal._id
    )

    if (request.ok) {
      appDispatch({
        type: 'updateShouldLoadPersonalPdp',
        payload: true,
      })
    }
  }

  const getResultHeader = () => {
    if (!company) {
      return
    }

    const map = new Map<number, ICompanyMember[]>()
    let total = 0
    let count = 0

    goal.journals.forEach(({ evaluationValue, senderId }) => {
      if (!evaluationValue) {
        return
      }

      total += evaluationValue
      count += 1

      if (!map.has(evaluationValue)) {
        map.set(evaluationValue, [])
      }
      map
        .get(evaluationValue)!
        .push(company.allMembers.find((m) => m._id === senderId)!)
    })

    return (
      <EvaluationHeader
        isOwnProfile={user?.asCompanyMember?._id === member._id}
        variant={'goals'}
        done={evaluationDone}
        membersMap={map}
        skillAverage={total / count}
      />
    )
  }

  return (
    <Card className={className} data-cy="goal-card">
      <CardHeader headerType={goalType} tags={tags} contextMenu={contextMenu} />
      <Inner>
        {!isInEditMode && (
          <>
            <RowAligned margin="0 0 12px">
              <IconContainer>
                <Icon type="Goals" color={theme.colors.textLight} size={20} />
              </IconContainer>
              <Text textStyle="title">{title}</Text>
            </RowAligned>
            <Fold
              caption={t('valueCard.description')}
              opened={!!openDescription}
              content={
                <StyledMarkDownText marginTop={8}>
                  {description}
                </StyledMarkDownText>
              }
            />
          </>
        )}
        {isInEditMode && renderEditMode}
        {goal.journals.length > 0 && (
          <Fold
            amount={withEvaluationResults ? undefined : notificationNumber}
            caption={
              withEvaluationResults
                ? t('evaluationModal.titles.termEvaluationResults')
                : t('personalGoal.notes')
            }
            opened={!!openJournals}
            showNotification={showNotification}
            onClick={openJournalFnc}
            content={
              goal.journals && (
                <StyledJournal
                  noMaxHeight={withEvaluationResults}
                  journals={goal.journals}
                  startJournalComponent={
                    withEvaluationResults && getResultHeader()
                  }
                />
              )
            }
          />
        )}
        {!bottomContent &&
          pdpStatus &&
          canMoreMessagesBeSentInPdpChat() &&
          submitMessage && (
            <Formik
              initialValues={{
                message: '',
              }}
              onSubmit={(values, options) => {
                submitMessage && submitMessage(goal._id, values.message)
                options.resetForm()
              }}
            >
              {({ submitForm, resetForm, values }) => {
                return (
                  <StyledForm>
                    <StyledAvatar user={isOwnProfile ? member : reviewer} />
                    <StyledInput
                      name="message"
                      placeholder={t(`personalGoal.placeholder`)}
                      size="default"
                      button={{
                        label: t('general.actions.send'),
                        handleClick: () => {
                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          submitForm().then(() => {
                            resetForm()
                          })
                        },
                        disabled: !values.message,
                      }}
                    />
                  </StyledForm>
                )
              }}
            </Formik>
          )}
        {bottomContent && <>{bottomContent}</>}
      </Inner>
      {cta && (
        <CTA onClick={cta.onClick}>
          <Text textStyle="labelHeavy" marginRight={8}>
            {cta.label}
          </Text>
          {cta.icon && (
            <Icon
              type={cta.icon}
              size={16}
              color={hsla(theme.colors.primary, 0.5)}
            />
          )}
        </CTA>
      )}
    </Card>
  )
}

const StyledMarkDownText = styled(MarkDownText)`
  opacity: 0.85;
`

const Card = styled.div`
  border-radius: 12px;
  box-shadow: ${shadows.depthTwo};
  overflow: hidden;
`

const CTA = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.colors.lightest};
  align-items: center;
  padding: 12px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.textLightest};
  cursor: pointer;

  > p {
    color: ${({ theme }) => hsla(theme.colors.primary, 0.5)};
  }

  &:hover {
    > p {
      color: ${({ theme }) => hsla(theme.colors.primary, 1)};
    }

    svg > path {
      fill: ${({ theme }) => hsla(theme.colors.primary, 1)};
    }
  }
`

const StyledJournal = styled(GoalJournal)<{ noMaxHeight?: boolean }>`
  ${({ noMaxHeight }) =>
    noMaxHeight &&
    `
    max-height: initial;
  `}
`

const Inner = styled.div`
  background-color: ${({ theme }) => theme.colors.lightest};
  padding: 16px;

  ${mqFrom.M`
    padding: 24px;
  `}
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.textLightest};
  margin-right: 16px;
`

const StyledForm = styled(Form)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`

const StyledAvatar = styled(Avatar)`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  margin-right: 12px;
`

const StyledInput = styled(Input)`
  width: 100%;

  textarea {
    background-color: ${({ theme }) => theme.colors.primaryLight};
    border: none;
  }
`

const Header = styled.div<{ bgColor: string; withBorder?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 24px;
  height: 44px;
  position: relative;
  background-color: ${({ bgColor }) => bgColor};

  ${({ withBorder, theme }) =>
    withBorder &&
    `
    border-bottom: 1px solid ${theme.colors.textLightest};
  `}
`

const StyledText = styled(Text)<{ textColor: string }>`
  color: ${({ textColor }) => textColor};
`

const Tags = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-left: auto;
`

const StyledButton = styled(Button)<{ newTextColor: string }>`
  background: transparent;
  margin-left: auto;
  color: ${({ newTextColor }) => newTextColor};
`

export default GoalCard
